// AuthGuard — gate for protected routes.
// Loading: render a quiet placeholder so the layout doesn't jump.
// Unauthenticated: redirect to /auth/login with the original path
// preserved as ?next= so we can return after sign-in.

import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { buildLoginHref } from './redirect'
import AuthRequiredState from '../components/AuthRequiredState'

interface AuthGuardProps {
  children: ReactNode
  mode?: 'redirect' | 'inline'
  title?: string
  body?: string
}

export default function AuthGuard({
  children,
  mode = 'redirect',
  title = 'Sign in required',
  body = 'Authenticate to continue.',
}: AuthGuardProps) {
  const { user, loading } = useAuth()
  const location = useLocation()
  const next = `${location.pathname}${location.search}`

  if (loading) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: 'center',
          color: 'var(--olv-text-tertiary)',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.10em',
        }}
      >
        AUTHENTICATING…
      </div>
    )
  }

  if (!user) {
    if (mode === 'inline') {
      return <AuthRequiredState title={title} body={body} next={next} />
    }
    return <Navigate to={buildLoginHref(next)} replace />
  }

  return <>{children}</>
}
